import {
  type CollectionType,
  type Command,
  collection,
  Decoration,
  DecorationSet,
  Plugin,
  PluginKey,
  type PMNode,
} from '@bangle.io/prosemirror-plugins';
import {
  type CommentThread,
  createComment,
  deleteComment,
  renderMentionBody,
  replyToComment,
} from '@bangle.io/supabase';
import type { EditorState, Transaction } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { isBlockActionEvent, markEditorChrome } from './block-action-button';
import { getActiveCollabSession, getMentionCandidates } from './collab-state';
import { attachMentionInput } from './mention-input';

const MARK_NAME = 'docComment';

type ComposeState = {
  from: number;
  to: number;
  quote: string;
};

type CommentPluginState = {
  threads: CommentThread[];
  userId: string | null;
  compose: ComposeState | null;
  expandedId: string | null;
  decorations: DecorationSet;
};

type CommentPluginMeta =
  | { type: 'threads'; threads: CommentThread[]; userId: string | null }
  | {
      type: 'compose';
      compose: ComposeState | null;
      expandedId?: string | null;
    }
  | { type: 'expand'; expandedId: string | null };

const commentPluginKey = new PluginKey<CommentPluginState>('sn-doc-comment');

export function setupDocComment() {
  const plugin = {
    docComment: new Plugin<CommentPluginState>({
      key: commentPluginKey,
      state: {
        init: (_, state) => {
          const empty: CommentPluginState = {
            threads: [],
            userId: null,
            compose: null,
            expandedId: null,
            decorations: DecorationSet.empty,
          };
          return {
            ...empty,
            decorations: buildDecorations(state.doc, empty),
          };
        },
        apply(tr, prev, _oldState, newState) {
          const meta = tr.getMeta(commentPluginKey) as
            | CommentPluginMeta
            | undefined;
          let next: CommentPluginState = prev;

          if (meta?.type === 'threads') {
            next = {
              ...next,
              threads: meta.threads,
              userId: meta.userId,
            };
          }
          if (meta?.type === 'compose') {
            next = {
              ...next,
              compose: meta.compose,
              ...(meta.expandedId !== undefined
                ? { expandedId: meta.expandedId }
                : {}),
            };
          }
          if (meta?.type === 'expand') {
            next = { ...next, expandedId: meta.expandedId };
          }

          const needsRebuild =
            Boolean(meta) ||
            tr.docChanged ||
            next.threads !== prev.threads ||
            next.compose !== prev.compose ||
            next.expandedId !== prev.expandedId ||
            next.userId !== prev.userId;

          if (!needsRebuild) {
            return {
              ...next,
              decorations: prev.decorations.map(tr.mapping, tr.doc),
            };
          }

          const mappedCompose = next.compose
            ? {
                from: tr.mapping.map(next.compose.from, -1),
                to: tr.mapping.map(next.compose.to, 1),
                quote: next.compose.quote,
              }
            : null;

          const withCompose = { ...next, compose: mappedCompose };
          return {
            ...withCompose,
            decorations: buildDecorations(newState.doc, withCompose),
          };
        },
      },
      props: {
        decorations(state) {
          return commentPluginKey.getState(state)?.decorations;
        },
        handleKeyDown(_view, event) {
          const mod = event.metaKey || event.ctrlKey;
          if (!mod || !event.altKey) return false;
          if (event.key !== 'm' && event.key !== 'M') return false;
          if (!getActiveCollabSession()) return false;
          event.preventDefault();
          return startCommentCompose()(_view.state, _view.dispatch, _view);
        },
      },
    }),
  };

  return collection({
    id: 'doc-comment',
    marks: {
      [MARK_NAME]: {
        attrs: {
          commentId: { default: null },
        },
        inclusive: false,
        excludes: '',
        parseDOM: [
          {
            tag: 'span[data-comment-id]',
            getAttrs: (dom) => {
              if (!(dom instanceof HTMLElement)) return false;
              const commentId = dom.getAttribute('data-comment-id');
              return commentId ? { commentId } : false;
            },
          },
        ],
        toDOM: (mark) => [
          'span',
          {
            class: 'sn-doc-comment',
            'data-comment-id': mark.attrs.commentId,
          },
          0,
        ],
      },
    },
    plugin,
    markdown: markdown(),
  });
}

export function setCommentThreads(
  threads: CommentThread[],
  userId: string | null,
): Command {
  return (state, dispatch) => {
    if (!dispatch) return Boolean(commentPluginKey.getState(state));
    dispatch(
      state.tr.setMeta(commentPluginKey, {
        type: 'threads',
        threads,
        userId,
      } satisfies CommentPluginMeta),
    );
    return true;
  };
}

export function startCommentCompose(): Command {
  return (state, dispatch) => {
    if (!getActiveCollabSession()) return false;
    const { from, to, empty } = state.selection;
    if (empty) return false;
    const quote = state.doc.textBetween(from, to, ' ').trim();
    if (!quote) return false;
    if (!dispatch) return true;
    dispatch(
      state.tr
        .setMeta(commentPluginKey, {
          type: 'compose',
          compose: { from, to, quote },
        } satisfies CommentPluginMeta)
        .scrollIntoView(),
    );
    return true;
  };
}

export function cancelCommentCompose(): Command {
  return (state, dispatch) => {
    const pluginState = commentPluginKey.getState(state);
    if (!pluginState?.compose) return false;
    if (!dispatch) return true;
    dispatch(
      state.tr.setMeta(commentPluginKey, {
        type: 'compose',
        compose: null,
      } satisfies CommentPluginMeta),
    );
    return true;
  };
}

function markdown(): CollectionType['markdown'] {
  return {
    marks: {
      [MARK_NAME]: {
        toMarkdown: {
          open: '',
          close: '',
          mixable: true,
          expelEnclosingWhitespace: true,
        },
      },
    },
  };
}

function buildDecorations(
  doc: PMNode,
  pluginState: Omit<CommentPluginState, 'decorations'>,
): DecorationSet {
  const decorations: Decoration[] = [];
  const markRanges = collectCommentMarkRanges(doc);
  const openThreads = pluginState.threads.filter((t) => !t.resolved);
  const threadById = new Map(openThreads.map((t) => [t.id, t]));

  for (const [commentId, range] of markRanges) {
    const thread = threadById.get(commentId);
    if (!thread) continue;
    const mine = Boolean(
      pluginState.userId && thread.createdBy === pluginState.userId,
    );
    const tone = mine ? 'mine' : 'theirs';

    decorations.push(
      Decoration.inline(range.from, range.to, {
        class: `sn-doc-comment sn-doc-comment--${tone}`,
      }),
    );

    const widgetPos = blockEndPos(doc, range.to);
    decorations.push(
      Decoration.widget(
        widgetPos,
        (view) =>
          createThreadWidget(
            view,
            thread,
            tone,
            pluginState.userId,
            pluginState.expandedId === commentId,
          ),
        {
          key: `sn-comment-thread-${commentId}-${pluginState.expandedId === commentId ? 'open' : 'closed'}`,
          side: 1,
          ignoreSelection: true,
          stopEvent: (event) =>
            isBlockActionEvent(event) ||
            event.type === 'keydown' ||
            event.type === 'input' ||
            event.type === 'keyup',
        },
      ),
    );
  }

  if (pluginState.compose) {
    const { from, to, quote } = pluginState.compose;
    const safeFrom = Math.max(0, Math.min(from, doc.content.size));
    const safeTo = Math.max(safeFrom, Math.min(to, doc.content.size));
    decorations.push(
      Decoration.inline(safeFrom, safeTo, {
        class: 'sn-doc-comment sn-doc-comment--compose',
      }),
    );
    decorations.push(
      Decoration.widget(
        blockEndPos(doc, safeTo),
        (view) =>
          createComposerWidget(
            view,
            quote,
            safeFrom,
            safeTo,
            pluginState.userId,
          ),
        {
          key: 'sn-comment-compose',
          side: 2,
          ignoreSelection: true,
          stopEvent: (event) =>
            isBlockActionEvent(event) ||
            event.type === 'keydown' ||
            event.type === 'input' ||
            event.type === 'keyup',
        },
      ),
    );
  }

  return DecorationSet.create(doc, decorations);
}

function collectCommentMarkRanges(
  doc: PMNode,
): Map<string, { from: number; to: number }> {
  const ranges = new Map<string, { from: number; to: number }>();
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    for (const mark of node.marks) {
      if (mark.type.name !== MARK_NAME) continue;
      const commentId = mark.attrs.commentId as string | null;
      if (!commentId) continue;
      const from = pos;
      const to = pos + node.nodeSize;
      const existing = ranges.get(commentId);
      if (!existing) {
        ranges.set(commentId, { from, to });
      } else {
        ranges.set(commentId, {
          from: Math.min(existing.from, from),
          to: Math.max(existing.to, to),
        });
      }
    }
  });
  return ranges;
}

function blockEndPos(doc: PMNode, pos: number): number {
  const clamped = Math.max(1, Math.min(pos, doc.content.size));
  const $pos = doc.resolve(clamped);
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    if ($pos.node(depth).isTextblock) {
      return $pos.after(depth);
    }
  }
  if ($pos.depth === 0) return clamped;
  return $pos.after(1);
}

function setExpandedThread(expandedId: string | null): Command {
  return (state, dispatch) => {
    if (!dispatch) return true;
    dispatch(
      state.tr.setMeta(commentPluginKey, {
        type: 'expand',
        expandedId,
      } satisfies CommentPluginMeta),
    );
    return true;
  };
}

function createThreadWidget(
  view: EditorView,
  thread: CommentThread,
  tone: 'mine' | 'theirs',
  userId: string | null,
  expanded: boolean,
): HTMLElement {
  const root = document.createElement('div');
  root.className = `sn-inline-thread sn-inline-thread--${tone}${expanded ? ' sn-inline-thread--expanded' : ' sn-inline-thread--collapsed'}`;
  markEditorChrome(root);

  for (const reply of thread.replies) {
    const body = document.createElement('p');
    body.className = 'sn-inline-thread-body';
    renderMentionBody(body, reply.body, reply.mentions);
    root.appendChild(body);
  }

  if (!expanded) {
    root.setAttribute('role', 'button');
    root.tabIndex = 0;
    root.setAttribute('aria-expanded', 'false');
    root.setAttribute('aria-label', 'Open comment');
    root.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setExpandedThread(thread.id)(view.state, view.dispatch, view);
    });
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setExpandedThread(thread.id)(view.state, view.dispatch, view);
      }
    });
    return root;
  }

  root.setAttribute('role', 'group');
  root.setAttribute('aria-expanded', 'true');
  root.setAttribute('aria-label', 'Comment thread');

  const replyRow = document.createElement('div');
  replyRow.className = 'sn-inline-thread-reply';
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'sn-inline-thread-input';
  input.placeholder = 'Reply… (@ to mention)';
  input.setAttribute('aria-label', 'Reply to thread');
  const mention = attachMentionInput(input, getMentionCandidates);
  const replyBtn = document.createElement('button');
  replyBtn.type = 'button';
  replyBtn.textContent = 'Reply';
  replyBtn.className = 'sn-inline-thread-btn';
  replyBtn.addEventListener('click', (event) => {
    event.stopPropagation();
    const body = input.value.trim();
    const session = getActiveCollabSession();
    if (!body || !session) return;
    replyToComment(session.ydoc, thread.id, {
      body,
      createdBy: userId,
      mentions: mention.getMentions(),
    });
    input.value = '';
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !(event as KeyboardEvent).isComposing) {
      // Mention composer may already have prevented Enter.
      if (event.defaultPrevented) return;
      event.preventDefault();
      replyBtn.click();
    }
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault();
      setExpandedThread(null)(view.state, view.dispatch, view);
      view.focus();
    }
  });
  replyRow.appendChild(input);
  replyRow.appendChild(replyBtn);
  root.appendChild(replyRow);

  const actions = document.createElement('div');
  actions.className = 'sn-inline-thread-actions';
  const deleteBtn = document.createElement('button');
  deleteBtn.type = 'button';
  deleteBtn.textContent = 'Delete';
  deleteBtn.className = 'sn-inline-thread-btn sn-inline-thread-btn--ghost';
  deleteBtn.addEventListener('click', (event) => {
    event.stopPropagation();
    const session = getActiveCollabSession();
    if (!session) return;
    mention.destroy();
    deleteComment(session.ydoc, thread.id);
    removeCommentMarks(view, thread.id);
    setExpandedThread(null)(view.state, view.dispatch, view);
  });
  actions.appendChild(deleteBtn);
  root.appendChild(actions);

  const doc = view.dom.ownerDocument;
  const onPointerDown = (event: Event) => {
    if (!root.isConnected) {
      mention.destroy();
      doc.removeEventListener('pointerdown', onPointerDown, true);
      return;
    }
    const target = event.target;
    if (!(target instanceof Node) || root.contains(target)) return;
    // Keep open while using the mention suggest menu (portaled to body).
    if (target instanceof Element && target.closest('.sn-mention-suggest')) {
      return;
    }
    mention.destroy();
    setExpandedThread(null)(view.state, view.dispatch, view);
  };
  doc.addEventListener('pointerdown', onPointerDown, true);

  requestAnimationFrame(() => input.focus());
  return root;
}

function createComposerWidget(
  view: EditorView,
  quote: string,
  from: number,
  to: number,
  userId: string | null,
): HTMLElement {
  const root = document.createElement('div');
  root.className =
    'sn-inline-thread sn-inline-thread--compose sn-inline-thread--expanded';
  markEditorChrome(root);

  if (quote) {
    const quoteEl = document.createElement('p');
    quoteEl.className = 'sn-inline-thread-quote';
    quoteEl.textContent = `“${quote}”`;
    root.appendChild(quoteEl);
  }

  const input = document.createElement('textarea');
  input.className = 'sn-inline-thread-textarea';
  input.rows = 2;
  input.placeholder = 'Start a thread… (@ to mention)';
  input.setAttribute('aria-label', 'Comment');
  root.appendChild(input);
  const mention = attachMentionInput(input, getMentionCandidates);

  const actions = document.createElement('div');
  actions.className = 'sn-inline-thread-actions';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.className = 'sn-inline-thread-btn sn-inline-thread-btn--ghost';
  cancelBtn.addEventListener('click', () => {
    mention.destroy();
    cancelCommentCompose()(view.state, view.dispatch, view);
    view.focus();
  });

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.textContent = 'Comment';
  saveBtn.className = 'sn-inline-thread-btn';
  saveBtn.addEventListener('click', () => {
    const body = input.value.trim();
    const session = getActiveCollabSession();
    if (!body || !session) return;
    const markType = view.state.schema.marks[MARK_NAME];
    if (!markType) return;

    const id = createComment(session.ydoc, {
      quote,
      body,
      createdBy: userId,
      mentions: mention.getMentions(),
    });
    mention.destroy();

    let tr: Transaction = view.state.tr;
    const mappedFrom = Math.max(0, Math.min(from, tr.doc.content.size));
    const mappedTo = Math.max(mappedFrom, Math.min(to, tr.doc.content.size));
    tr = tr.addMark(mappedFrom, mappedTo, markType.create({ commentId: id }));
    tr = tr.setMeta(commentPluginKey, {
      type: 'compose',
      compose: null,
      expandedId: null,
    } satisfies CommentPluginMeta);
    view.dispatch(tr.scrollIntoView());
    view.focus();
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault();
      cancelBtn.click();
    }
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      saveBtn.click();
    }
  });

  actions.appendChild(cancelBtn);
  actions.appendChild(saveBtn);
  root.appendChild(actions);

  requestAnimationFrame(() => input.focus());
  return root;
}

function removeCommentMarks(view: EditorView, commentId: string) {
  const markType = view.state.schema.marks[MARK_NAME];
  if (!markType) return;
  const tr = view.state.tr;
  view.state.doc.descendants((node, pos) => {
    if (!node.isText) return;
    if (
      node.marks.some(
        (m) => m.type === markType && m.attrs.commentId === commentId,
      )
    ) {
      tr.removeMark(pos, pos + node.nodeSize, markType);
    }
  });
  if (tr.docChanged) {
    view.dispatch(tr);
  }
}

export function commentsEnabledInState(state: EditorState): boolean {
  return Boolean(getActiveCollabSession() && commentPluginKey.getState(state));
}

export { MARK_NAME as DOC_COMMENT_MARK_NAME };
